import { Flex, Text } from "@chakra-ui/react";
import _ from "lodash";
import { useMemo } from "react";
import { PersonAvatar } from "src/components/Layout/Avatar";
import { useGlossary } from "src/hooks/useGlossary";
import { WEGLOT_SKIP_CLASS } from "src/plugins/weglot/constants";
import * as Format from "src/services/format";
import * as GQL from "src/types/graphql";
import { FormTemplateCard } from "./FormTemplateCard";

type StudentFormTemplateCardsProps = {
  forms: GQL.GetParentFormsIndex_person_forms[];
  assignedForms: GQL.GetParentAssignedForms_assigned_form_next_form[];
  student: GQL.GetParentFormsIndex_person;
};

type SortedForm = GQL.GetParentFormsIndex_person_forms & { sortDate: string };

export function StudentFormTemplateCards(props: StudentFormTemplateCardsProps) {
  const { forms, student, assignedForms: assignedFormsNextForms } = props;

  const { glossary } = useGlossary();

  const fullName = Format.fullName(student) || glossary`Student`;
  const shortName = Format.shortName(student) || glossary`Student`;

  const sortedForms = useMemo(() => {
    const unsortedForms: SortedForm[] = [];

    forms.forEach((form) => {
      if (form.form_template.closed_at) {
        return unsortedForms.push({
          ...form,
          sortDate: form.form_template.closed_at,
        });
      }

      if (form.form_template.open_at) {
        return unsortedForms.push({
          ...form,
          sortDate: form.form_template.open_at,
        });
      }

      return unsortedForms.push({
        ...form,
        sortDate: "",
      });
    });
    return _.orderBy(unsortedForms, (form) => form.sortDate, "desc");
  }, [forms]);

  const assignedForms = useMemo(
    () =>
      assignedFormsNextForms.flatMap((af) => {
        // These fields should never be null, but since it's a view Postgres
        // doesn't properly tag them as non-nullable. Just in case, abort if the
        // form template is null.
        if (af.assigned_form_template === null) return [];
        return {
          id: af.assigned_form_id ?? "",
          form_template: af.assigned_form_template,
          person_id: af.person_id ?? "",
          previous_form_id: af.previous_form_id,
          previous_offer_id: af.previous_offer_id,
          previous_waitlist_id: af.previous_waitlist_id,
        };
      }),
    [assignedFormsNextForms]
  );

  return (
    <Flex flexDirection="column" gap={4}>
      <Flex alignItems="center" gap={2}>
        <PersonAvatar person={student} size="sm" />

        <Text
          as="h2"
          fontSize="lg"
          color="gray.700"
          className={WEGLOT_SKIP_CLASS}
          fontWeight={700}
        >
          {fullName}
        </Text>
      </Flex>

      {sortedForms.map((form) => (
        <FormTemplateCard
          actionItems={form.action_items}
          formTemplate={form.form_template}
          form={form}
          assignedForms={assignedForms}
          formSchoolRanks={form.form_school_ranks}
          enrollmentPeriod={form.form_template.enrollment_period}
          gradeLabel={form.grades_answers[0]?.grade_config?.label ?? ""}
          key={form.id}
          statusDescriptions={form.form_template.form_status_descriptions}
          studentName={shortName}
          rankingEnabled={
            form.form_template.sections.find(
              (section) =>
                section.type ===
                GQL.form_template_section_type_enum.SchoolRankingSection
            )?.schools_ranking_section?.ranking_enabled ?? false
          }
          showWaitlistPosition={form.form_template.show_waitlist_position}
        />
      ))}
    </Flex>
  );
}
